'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { BarChart3, Loader2 } from 'lucide-react';
import { auth } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

export default function LoginPage() {
  const router = useRouter();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const checkedRef = useRef(false);

  // Check if already authenticated on mount
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;
    
    async function checkExistingAuth() {
      try {
        const me = await auth.me();
        if (me) {
          console.log('Already authenticated, redirecting to dashboard');
          router.push('/dashboard');
        }
      } catch (err) {
        // Not authenticated - that's expected, stay on login page
        console.log('Not authenticated, showing login form');
      }
    }

    checkExistingAuth();
  }, [router]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login:', { email, rememberMe });
      
      const res = await auth.login({ email, password });
      
      if (!res || !res.token) {
        throw new Error('Login response is missing token');
      }

      // Store auth token in localStorage/sessionStorage
      if (rememberMe) {
        localStorage.setItem('auth_token', res.token);
      }
      sessionStorage.setItem('auth_token', res.token);

      // Update Zustand store
      setAuth(
        res.user || null, 
        res.workspace || null, 
        res.token
      );
      
      console.log('Login successful, navigating to dashboard');
      
      // Navigate to dashboard - no need to verify again, we just logged in!
      router.push('/dashboard');
    } catch (err: any) {
      console.error('Login error:', err);
      
      const errorMessage = err.message || 'Login failed';
      if (errorMessage.toLowerCase().includes('unauthorized') || 
          errorMessage.toLowerCase().includes('invalid')) {
        setError('Invalid email or password');
      } else if (errorMessage.toLowerCase().includes('network')) {
        setError('Network error. Please check your connection.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/" className="inline-flex items-center gap-2 text-2xl font-bold">
            <BarChart3 className="h-8 w-8 text-blue-500" />
            VizionAnalytics
          </Link>
        </div>

        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8">
          <h1 className="text-2xl font-bold mb-6">Welcome back</h1>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-blue-500"
                required
                autoComplete="email"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:border-blue-500"
                required
                autoComplete="current-password"
                placeholder="Enter your password"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center text-zinc-400">
                <input 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="mr-2 text-blue-500 focus:ring-blue-500"
                />
                Remember me
              </label>
              <Link 
                href="/forgot-password" 
                className="text-blue-400 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg font-medium flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Log in
            </button>
          </form>

          <p className="mt-4 text-center text-zinc-400 text-sm">
            Don&apos;t have an account?{' '}
            <Link href="/signup" className="text-blue-400 hover:underline">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
